// The gust's ages: for each live wave and each cell of the field's grid, how long (in the
// grains' simulation time) since the wave reached that cell, smoothed the same way as the
// field's palette. The field's lift pass writes them, one byte per wave (field-shaders.ts),
// and the field reads them back a frame late (field.ts). Everything a wave reaches can
// measure from them.
//
// The grains are off for now (`GUST_GRAINS` in field.ts), so there are no ages, and the
// other parts follow the plain gust front instead.

import type { Box } from "@bfly/engine";

/** The longest age a byte can hold, in seconds of simulation time. It's past the longest band
    behind a front (the words' trough). Anything older reads as this. */
export const AGE_RANGE = 1;
/** How much simulation time one band thickness takes, for the words a wave restyles from
    the ages. */
export const AGE_BAND = 0.12;
const AGE_STEPS = 254;

/** The ages the field read back:
    - the waves' ids, one channel each
    - the grid, in viewport CSS pixels
    - RGBA data, one byte per channel, row by row from the top. A byte is 0 where the wave
      hasn't reached the cell yet, otherwise 1 plus the age as a fraction of `AGE_RANGE`, in
      `AGE_STEPS` steps. */
export interface WaveAges {
  readonly ids: readonly number[];
  readonly cols: number;
  readonly rows: number;
  readonly cellW: number;
  readonly cellH: number;
  readonly originX: number;
  readonly originY: number;
  readonly data: Uint8Array;
}

/** The field's ages before there's any wave. */
export const NO_AGES: WaveAges = Object.freeze({
  ids: [],
  cols: 0,
  rows: 0,
  cellW: 1,
  cellH: 1,
  originX: 0,
  originY: 0,
  data: new Uint8Array(0),
});

/** An age, packed into a byte. */
export function encodeAge(age: number): number {
  return 1 + Math.round(Math.min(Math.max(age / AGE_RANGE, 0), 1) * AGE_STEPS);
}

/** A byte unpacked into an age, or null if the wave hasn't reached the cell. */
export function decodeAge(byte: number): number | null {
  return byte === 0 ? null : ((byte - 1) / AGE_STEPS) * AGE_RANGE;
}

/** `encodeAge()` in GLSL, as a normalized byte, for the lift pass. */
export const AGE_GLSL = `
float encodeAge(float age) {
  return (1.0 + floor(clamp(age / ${AGE_RANGE.toFixed(1)}, 0.0, 1.0) * ${AGE_STEPS.toFixed(1)} + 0.5)) / 255.0;
}
`;

/** A wave's channel, or -1 if the ages don't have it (it started after the read). */
export function channelOf(ages: WaveAges, id: number): number {
  return ages.ids.indexOf(id);
}

function cellAge(ages: WaveAges, channel: number, col: number, row: number): number | null {
  if (channel < 0 || col < 0 || row < 0 || col >= ages.cols || row >= ages.rows) return null;
  return decodeAge(ages.data[(row * ages.cols + col) * 4 + channel] ?? 0);
}

/** The age of the cell under a point in the viewport, or null. */
export function ageAt(ages: WaveAges, channel: number, x: number, y: number): number | null {
  const col = Math.floor((x - ages.originX) / ages.cellW);
  const row = Math.floor((y - ages.originY) / ages.cellH);
  return cellAge(ages, channel, col, row);
}

/** Whether any cell in a box has an age between `from` and `to`. */
export function bandOnBox(
  ages: WaveAges,
  channel: number,
  box: Box,
  from: number,
  to: number,
): boolean {
  if (channel < 0) return false;
  // The right and bottom edges are exclusive: a box that ends exactly on a cell's edge doesn't
  // cover that cell. An empty box still covers the cell it sits in.
  const first = Math.floor((box.left - ages.originX) / ages.cellW);
  const top = Math.floor((box.top - ages.originY) / ages.cellH);
  const last = Math.max(first, Math.ceil((box.right - ages.originX) / ages.cellW) - 1);
  const bottom = Math.max(top, Math.ceil((box.bottom - ages.originY) / ages.cellH) - 1);
  for (let row = Math.max(0, top); row <= Math.min(ages.rows - 1, bottom); row++) {
    for (let col = Math.max(0, first); col <= Math.min(ages.cols - 1, last); col++) {
      const age = cellAge(ages, channel, col, row);
      if (age !== null && age >= from && age <= to) return true;
    }
  }
  return false;
}

/** Whether a wave has reached any cell in a box. */
export function boxReached(ages: WaveAges, channel: number, box: Box): boolean {
  return bandOnBox(ages, channel, box, 0, Infinity);
}
